'use client';

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import { CloseButton } from '@web/atoms';
import { ensureGladlyInitialized } from '../../gladly/ensureGladlyInitialized';
import { useReporter } from '../../reporter';
import { useSessionCustomer } from '../../customer';
import { useExperiment, HIDE_GLADLY_WIDGET } from '../../experiments';
import { ChatIcon } from './ChatIcon';
import {
  ButtonContainer,
  Button,
  UnreadMessageDot,
  ProactiveMessage,
  CloseButtonContainer,
} from './styles';
import { OVERLAPPING_PATH } from './constants';
import { userClickGladyChatButton } from './events';

export const ChatButton = ({
  onboardingUI = 'control',
  gladlySettings: { displayRules = { rules: [] } } = {},
}) => {
  const reporter = useReporter();
  const [isGladlyInitiated, setIsGladlyInitiated] = useState(false);
  const [showUnreadMessageDot, setShowUnreadMessageDot] = useState(false);
  const [campaignData, setCampaignData] = useState(null);
  const [showProactiveMessage, setShowProactiveMessage] = useState(false);
  const {
    isLoading: isCustomerLoading,
    isAuthenticated,
    customer,
  } = useSessionCustomer();
  const router = useRouter();

  const findOverlappingPath = pathname => {
    if (pathname === undefined) {
      return false;
    }
    const result = OVERLAPPING_PATH.find(path => pathname.indexOf(path) !== -1);
    return result !== undefined;
  };

  const isOverlapping = findOverlappingPath(router.asPath);

  //Checks if the current url is in the excluded list of Gladly settings page
  const isExcludedPath = displayRules?.rules.some(item =>
    router.asPath.includes(item.content.value),
  );
  //CRO Test: Hide Gladly Widget on Onboarding
  const { value: hideGladlyWidget } = useExperiment(HIDE_GLADLY_WIDGET, false);
  const isOnboardingPath = router.asPath.includes('onboarding');
  const hideOnOnboarding = hideGladlyWidget && isOnboardingPath;
  const isOnboardingOrSampler =
    router.asPath.includes('onboarding') ||
    router.asPath.includes('sampler-box');
  useEffect(() => {
    let cancelled = false;
    let started = false;
    let subscribed = false;
    /** @type {ReturnType<typeof setInterval> | undefined} */
    let waitId;

    const routeChangeHandler = () => window.Gladly?.navigate();

    const onMessageReceived = () => {
      setShowUnreadMessageDot(true);
    };
    const onCampaignTriggered = event => {
      setShowUnreadMessageDot(true);
      setCampaignData(event[0]);
      setShowProactiveMessage(true);
    };
    const onSidekickOpened = () => {
      setShowUnreadMessageDot(false);
    };

    const unsubscribe = () => {
      const { Gladly } = window;
      if (!Gladly || typeof Gladly.off !== 'function') return;
      try {
        Gladly.off('message:received', onMessageReceived);
        Gladly.off('campaign:triggered', onCampaignTriggered);
        Gladly.off('sidekick:opened', onSidekickOpened);
      } catch {
        // Unmount should not surface a Gladly error.
      }
    };

    const start = () => {
      if (cancelled || started) return;
      const pending = ensureGladlyInitialized();
      if (!pending) return;

      started = true;
      pending
        .then(() => {
          if (cancelled) return;
          const { Gladly } = window;
          if (!Gladly || typeof Gladly.on !== 'function') {
            started = false;
            return;
          }
          try {
            Gladly.on('message:received', onMessageReceived);
            Gladly.on('campaign:triggered', onCampaignTriggered);
            Gladly.on('sidekick:opened', onSidekickOpened);
            router.events.on('routeChangeComplete', routeChangeHandler);
          } catch {
            unsubscribe();
            router.events.off('routeChangeComplete', routeChangeHandler);
            if (!cancelled) started = false;
            return;
          }
          subscribed = true;
          setIsGladlyInitiated(true);
          clearInterval(waitId);
        })
        .catch(() => {
          // The failed attempt is already forgotten. Keep polling so the
          // button can subscribe on the next try instead of staying hidden.
          if (cancelled || subscribed) return;
          started = false;
        });
    };

    waitId = setInterval(start, 100);
    start();

    return () => {
      cancelled = true;
      clearInterval(waitId);
      router.events.off('routeChangeComplete', routeChangeHandler);
      if (subscribed) unsubscribe();
    };
  }, [router.events]);

  useEffect(() => {
    const { Gladly } = window;

    const isGladlyAvailable = Gladly;
    if (
      !isCustomerLoading &&
      customer &&
      isAuthenticated &&
      isGladlyAvailable &&
      isGladlyInitiated
    ) {
      const { first_name, email } = customer;
      if (Gladly.clearUser && Gladly.setUser && first_name && email) {
        Gladly.clearUser();
        Gladly.setUser({
          name: first_name,
          email,
        });
      }
    }
  }, [customer, isAuthenticated, isCustomerLoading, isGladlyInitiated]);

  const onChatButtonHandler = () => {
    reporter.tag(userClickGladyChatButton({ path: router.asPath }));
    const { Gladly } = window;
    if (Gladly.show) {
      if (campaignData) {
        Gladly.applyCampaign(campaignData.id);
        setCampaignData(null);
      } else Gladly.show();
    }
  };

  const onCloseButtonClick = e => {
    e.stopPropagation();
    setShowProactiveMessage(false);
  };

  if (isExcludedPath || !isGladlyInitiated || hideOnOnboarding) {
    return <></>;
  }

  return (
    <ButtonContainer
      shown={true}
      $isControl={onboardingUI === 'control' ? true : false}
      isOverlapping={isOverlapping}
    >
      {showProactiveMessage && campaignData && (
        <>
          <ProactiveMessage
            className="campaign-message"
            onClick={onChatButtonHandler}
          >
            {campaignData.actions[0].content.message}
            <CloseButtonContainer onClick={onCloseButtonClick}>
              <CloseButton color="border.dark" />
            </CloseButtonContainer>
          </ProactiveMessage>
        </>
      )}
      <Button
        id={'custom-gladly-chat-button'}
        aria-label={
          'Click here to chat with a customer service representative.'
        }
        $useLightVariant={isOnboardingOrSampler}
        onClick={onChatButtonHandler}
      >
        <ChatIcon fillColor={isOnboardingOrSampler ? 'neutral.dark' : null} />
      </Button>

      {showUnreadMessageDot && <UnreadMessageDot />}
    </ButtonContainer>
  );
};

ChatButton.displayName = 'ChatButton';

ChatButton.propTypes = {
  onboardingUI: PropTypes.string,
  gladlySettings: PropTypes.object,
};
